// Coerce a date from any common source (already-ISO, dd-mm-yyyy, dd/mm/yyyy,
// or anything the Date constructor can parse) into the `yyyy-mm-dd` string that
// both an <input type="date"> and the backend's Pydantic `date` schema require.
//
// Why this exists: the registration PDF-import fills date fields with whatever
// text it extracted (e.g. "12-05-2015"). A non-ISO value makes the date input
// render blank while still being truthy, so the required-field check passes and
// the value is POSTed verbatim — which the API rejects with a 422. Normalising
// on import (and again when building the payload) closes that gap.
//
// Ambiguous d?m?y shapes are read DAY-FIRST: the app's date inputs render
// dd-mm-yyyy and the centres using it write dates that way. Where day-first is
// IMPOSSIBLE the month-first reading is taken instead - a US-formatted export
// ("7/16/2015") is unambiguous, and used to be discarded.
// Returns '' when the value can't be understood, so callers can treat it as a
// missing date rather than forward something invalid.
export const toISODate = (value) => {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';

  // Already ISO.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // dd-mm-yyyy or dd/mm/yyyy (day-first), and mm/dd/yyyy where day-first cannot be.
  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const first = Number(dmy[1]);
    const second = Number(dmy[2]);
    const year = dmy[3];
    const usable = (day, mon) => mon >= 1 && mon <= 12 && day >= 1 && day <= 31;
    const iso = (day, mon) => `${year}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Day-first first, for the reason given above.
    if (usable(first, second)) return iso(first, second);

    // MONTH-FIRST WHEN DAY-FIRST IS IMPOSSIBLE. "7/16/2015" cannot be the 7th of
    // the 16th month, so it can only be M/D/Y - and that is exactly what a centre's
    // own Word export writes. This used to hit the range check, return '', and drop
    // the child's date of birth silently: the field rendered empty while age filled
    // in beside it, so the import looked like it had worked.
    //
    // NOT a change of policy. An ambiguous date - 07/08/2015, where both readings
    // are real dates - is still read day-first and never reaches this line.
    if (usable(second, first)) return iso(second, first);

    return '';
  }

  // Anything else the engine can parse (e.g. "May 12, 2015"). Use local date
  // parts so a timezone offset can't shift the day.
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const mon = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${mon}-${day}`;
  }

  return '';
};

// True when a session's scheduled date falls on a day AFTER today (local time).
//
// Data collection is blocked for future-dated sessions: an event may be CREATED
// for a future day, but no data can be recorded until that day arrives. Today and
// past days return false (a therapist may back-fill a missed/past session). The
// backend is the authoritative guard (see sessions.py / data_collection.py); this
// is the UX gate so the user gets a clear message instead of a 400.
//
// Accepts a Date (calendar event.start), an ISO 'yyyy-mm-dd' or 'yyyy-mm-ddT…'
// string (session.date), or anything the Date constructor understands. A pure ISO
// date string is read by its calendar parts to avoid a UTC→local day shift; an
// unparseable value returns false (fail-open on the client — the API still blocks).
export const isFutureSessionDate = (value) => {
  if (!value) return false;
  let sessionDay;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return false;
    sessionDay = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  } else {
    const s = String(value).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      sessionDay = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    } else {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return false;
      sessionDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return sessionDay.getTime() > today.getTime();
};

// Resolve a calendar event / session object to its scheduled START as a Date
// carrying the time. Prefers an explicit `date` ('yyyy-mm-dd') + `startTime`
// ('HH:MM' 24-hour) — unambiguous local time — and falls back to an existing
// `start` Date/ISO. Returns null when nothing usable is present, so callers can
// treat a timeless event as "no window" rather than gating on a bad value.
export const scheduledStartOf = (ev) => {
  if (!ev) return null;
  if (ev.date && ev.startTime && /^\d{1,2}:\d{2}$/.test(String(ev.startTime).trim())) {
    const d = new Date(`${ev.date}T${String(ev.startTime).trim()}:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (ev.start) {
    const d = ev.start instanceof Date ? ev.start : new Date(ev.start);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

// True when a session is still TOO EARLY to conduct/attend: `now` is more than
// `graceMinutes` before its scheduled START time. A session opens for
// starting / data collection / attendance only within a grace window before it
// begins (default 1 hour) — e.g. a 6pm session opens at 5pm.
//
// This layers a same-day TIME gate on top of the future-DATE block
// (isFutureSessionDate): a future-dated session is blocked all day (its start
// minus the grace is still in the future), and a session later today is blocked
// until the window opens. Past/started sessions return false (now is well past
// the threshold), so back-filling a missed session is unaffected.
//
// `value` must carry a TIME to be meaningful — pass a Date (calendar
// `event.start`) or an ISO 'yyyy-mm-ddThh:mm' string. A date-only value has no
// start time, so the threshold lands on midnight and the check effectively
// defers to the day-level guard. Unparseable/empty → false (fail-open on the
// client; the day-level gate and the backend still apply).
export const isBeforeConductWindow = (value, graceMinutes = 60) => {
  if (!value) return false;
  const start = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(start.getTime())) return false;
  return Date.now() < start.getTime() - graceMinutes * 60 * 1000;
};

// Turn a FastAPI error (or our thrown {detail,status}) into a short, readable
// string. FastAPI 422 bodies are `detail: [{loc, msg, ...}]`; without this a
// toast renders "[object Object]" and hides which field failed.
export const formatApiError = (err, fallback = 'Please try again.') => {
  const detail = err?.detail ?? err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== 'body').join('.') : e?.loc;
        return loc ? `${loc}: ${e?.msg || 'invalid'}` : e?.msg || 'invalid';
      })
      .join('; ');
  }
  if (typeof detail === 'string') return detail;
  return err?.message || fallback;
};
